import React, { useEffect, useState } from 'react';
import ScreenshotHomeDark from '../images/dark/home_dark_blue.png';
import { FiBookOpen, FiCheckSquare, FiLock, FiZap, FiInfo } from 'react-icons/fi';
import ScreenshotHomeLight from '../images/light/home_light_green.png';

function getInitialIsDark() {
  if (typeof window === 'undefined') return false;
  // prefer stored user choice, then explicit class on document, then system preference
  try {
    const stored = localStorage.getItem('pulsecio_theme');
    if (stored === 'dark') return true;
    if (stored === 'light') return false;
  } catch (e) {}

  if (document.documentElement.classList.contains('dark')) return true;
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

const Hero = () => {
  const [isDark, setIsDark] = useState(getInitialIsDark());

  useEffect(() => {
    const handleThemeChange = (ev) => {
      // if event provides detail (our CustomEvent), use it
      if (ev && ev.detail) {
        setIsDark(ev.detail === 'dark');
        return;
      }

      // prefer stored setting
      try {
        const stored = localStorage.getItem('pulsecio_theme');
        if (stored === 'dark') { setIsDark(true); return; }
        if (stored === 'light') { setIsDark(false); return; }
      } catch (e) {}

      // fallback to document class
      if (document.documentElement.classList.contains('dark')) { setIsDark(true); return; }

      // finally fall back to system preference
      const prefersDarkNow = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDark(!!prefersDarkNow);
    };

    // listen to explicit theme-change events dispatched by theme helpers
    window.addEventListener('theme-change', handleThemeChange);

    // listen to storage so toggles from other tabs update this tab
    const onStorage = (e) => { if (!e.key || e.key === 'pulsecio_theme') handleThemeChange(); };
    window.addEventListener('storage', onStorage);

    // listen to system preference changes
    const mql = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
    if (mql) {
      if (mql.addEventListener) mql.addEventListener('change', handleThemeChange);
      else if (mql.addListener) mql.addListener(handleThemeChange);
    }

    return () => {
      window.removeEventListener('theme-change', handleThemeChange);
      window.removeEventListener('storage', onStorage);
      if (mql) {
        if (mql.removeEventListener) mql.removeEventListener('change', handleThemeChange);
        else if (mql.removeListener) mql.removeListener(handleThemeChange);
      }
    };
  }, []);

  const ScreenshotHome = isDark ? ScreenshotHomeDark : ScreenshotHomeLight;

  return (
    <section className="relative overflow-hidden">
      <div className="gradient-hero absolute inset-0 -z-10 opacity-90"></div>
      <div className="container mx-auto px-6 md:py-28 py-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 mb-3 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-sm font-semibold rounded-full shadow-sm animate-fadeIn">
              <FiZap className="w-4 h-4 shrink-0" /> Un seul outil qui remplace Notion + ClickUp + Confluence + outils de com
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight text-black dark:text-white animate-fadeInDown">Optimisez la communication de votre organisation</h1>

            <p className="text-lg text-gray-700 dark:text-gray-200 mb-6 animate-fadeIn delay-200">La plateforme unifiée pour <strong>communication structurée, gestion de tâches et collaboration</strong>. Idéale pour DSI & CIO Office, PME, associations, et toute organisation qui souhaite gagner du temps. Gérez messages, tâches, rappels et audits — sans jongler entre plusieurs outils.</p>

            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300 animate-fadeIn delay-300">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-white dark:bg-gray-800 shadow">
                  <FiBookOpen className="w-5 h-5 text-blue-500" />
                </span>
                <span><strong>Bibliothèque de messages</strong> — Créez, collaborez et diffusez du contenu structuré pour toute votre organisation</span>
              </li>
              <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300 animate-fadeIn delay-400">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-white dark:bg-gray-800 shadow">
                  <FiCheckSquare className="w-5 h-5 text-green-500" />
                </span>
                <span><strong>Tâches & Suivi</strong> — Gérez actions et projets, que vous soyez DSI, PME, association ou particulier</span>
              </li>
              <li className="flex items-start gap-3 text-gray-700 dark:text-gray-300 animate-fadeIn delay-500">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-white dark:bg-gray-800 shadow">
                  <FiLock className="w-5 h-5 text-yellow-500" />
                </span>
                <span><strong>Multi-tenant & Audit</strong> — Sécurité, conformité et traçabilité intégrées</span>
              </li>
            </ul>

            <div className="flex flex-col md:flex-row gap-3">
              <a href="#plans" className="flex-1 md:flex-none justify-center inline-flex items-center px-5 py-3 bg-white text-black rounded shadow hover:shadow-lg transition animate-scaleUp">Commencer — Voir les tarifs</a>
              <a href="https://doc.pulsecio.com" target="_blank" rel="noreferrer" className="flex-1 md:flex-none justify-center inline-flex items-center px-5 py-3 border border-gray-700 text-black dark:text-white rounded hover:bg-white/10 transition">Lire la documentation</a>
            </div>

            <div className="mt-8 px-4 py-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg text-sm text-gray-700 dark:text-gray-200 animate-fadeIn delay-600 flex items-start gap-2">
              <FiInfo className="w-4 h-4 shrink-0 mt-0.5 text-green-600 dark:text-green-400" />
              <span><strong>Économisez jusqu'à 2475€/mois</strong> en remplaçant 4 outils séparés par une solution tout-en-un à partir de 7,99€/mois</span>
            </div>
          </div>

          <div className="md:col-span-5 flex justify-center md:justify-end">
            <div className="w-full shadow-2xl rounded-xl p-2 overflow-hidden transform hover:scale-105 transition border border-white dark:border-gray-700">
              <img src={ScreenshotHome} alt="Aperçu interface PulseCIO, plateforme SaaS de communication DSI et CIO Office, gouvernance IT, tâches, messages, audit" width="1200" height="800" className="w-full block bg-white" loading="eager" fetchPriority="high" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
